import { SxProps, Theme } from '@mui/material';
import { ADD_PRODUCT_ROW_FIELD_HEIGHT } from '../../../orders.constants';
import { ORDER_PRODUCT_ROW_BUTTON_SIZE } from '../../../orders.constants';
import { ORDER_PRODUCT_ROW_INLINE_SIZES } from '../../orderProductsLayout.constants';
import { OrderProductRowLayout } from '../../orderProductsLayout.types';
import { isOrderProductRowInline } from '../../orderProductsLayout.utils';

const CODE_FIELD_MAX_WIDTH =
  ORDER_PRODUCT_ROW_INLINE_SIZES[OrderProductRowLayout.WIDE].imageAndLabel;
const CODE_FIELD_AND_SELECTS_GAP =
  ORDER_PRODUCT_ROW_INLINE_SIZES[OrderProductRowLayout.WIDE].columnGap;
const MOBILE_CODE_FIELD_AND_SELECTS_GAP = 8;

export const getAddRowSx = (layout: OrderProductRowLayout): SxProps<Theme> => ({
  alignItems: layout === OrderProductRowLayout.MOBILE ? 'flex-start' : 'center',
  alignContent: 'flex-start',
  flexWrap: isOrderProductRowInline(layout) ? 'nowrap' : 'wrap',
});

export const getAddRowInfoSx = (layout: OrderProductRowLayout): SxProps<Theme> => ({
  flexDirection: 'row',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  flexWrap: 'nowrap',
  '&&': {
    columnGap: `${
      layout === OrderProductRowLayout.MOBILE
        ? MOBILE_CODE_FIELD_AND_SELECTS_GAP
        : CODE_FIELD_AND_SELECTS_GAP
    }px`,
  },
});

export const getAddRowButtonsSx = (layout: OrderProductRowLayout): SxProps<Theme> => ({
  '& .MuiButton-endIcon svg': {
    visibility: 'visible',
  },
  '& .MuiButton-root:not([data-active="true"])': {
    backgroundColor: 'background.paper',
    '&:hover': {
      backgroundColor: 'background.paper',
    },
  },
  ...(layout === OrderProductRowLayout.MOBILE && {
    '&&': {
      flexDirection: 'column',
      flex: 1,
      minWidth: 0,
      width: 'auto',
    },
  }),
});

export const addRowTotalsSlotSx: SxProps<Theme> = { marginLeft: 'auto' };

export const addRowMobileGridRowSx: SxProps<Theme> = {
  order: 3,
  display: 'flex',
  width: '100%',
  minWidth: 0,
  flexWrap: 'nowrap',
  alignItems: 'center',
  columnGap: `${MOBILE_CODE_FIELD_AND_SELECTS_GAP}px`,
};

export const getAddRowGridSx = (layout: OrderProductRowLayout): SxProps<Theme> | undefined =>
  layout === OrderProductRowLayout.MOBILE
    ? { '&&': { flex: '1 1 0', minWidth: 0, overflowX: 'auto' } }
    : undefined;

export const getAddRowTotalsWrapperSx = (
  layout: OrderProductRowLayout,
): SxProps<Theme> | undefined =>
  layout === OrderProductRowLayout.MOBILE
    ? { '&&': { order: 4, flexShrink: 0, alignSelf: 'center' } }
    : undefined;

export const getAddRowButtonSlotSx = (hasTouchLayout: boolean): SxProps<Theme> => ({
  ...(hasTouchLayout ? {} : { width: ORDER_PRODUCT_ROW_BUTTON_SIZE }),
  flexShrink: 0,
  display: 'flex',
  justifyContent: 'center',
});

export const getCodeFieldBoxSx = (layout: OrderProductRowLayout): SxProps<Theme> => {
  if (layout === OrderProductRowLayout.MOBILE) {
    return { flex: 1, minWidth: 0 };
  }

  return {
    '&&': {
      flex: `0 1 ${CODE_FIELD_MAX_WIDTH}px`,
      maxWidth: CODE_FIELD_MAX_WIDTH,
      marginRight: 0,
    },
  };
};

export const codeFieldContainerSx: SxProps<Theme> = (theme) => ({
  position: 'relative',
  flex: 1,
  width: '100%',
  minWidth: 0,
  minHeight: `calc(${ADD_PRODUCT_ROW_FIELD_HEIGHT}px + ${theme.spacing(0.2)} + 2px)`,
  alignSelf: 'stretch',
  display: 'flex',
  alignItems: 'flex-start',
  borderRadius: `${theme.shape.borderRadiusMd}px`,
  border: `1px solid ${theme.palette.custom.border.primary}`,
  backgroundColor: 'background.paper',
  py: 0.1,
  px: 0.8,
  '&:hover': {
    borderColor: 'action.active',
  },
  '&:focus-within': {
    borderColor: 'text.primary',
  },
});

export const codeInputSx: SxProps<Theme> = (theme) => ({
  ...theme.typography.body2,
  color: 'text.primary',
  '& .MuiInputBase-input': {
    paddingRight: theme.spacing(3),
  },
  '& .MuiInputBase-input::placeholder': {
    color: theme.palette.text.secondary,
    opacity: 1,
  },
});

export const codeFieldStatusSlotSx: SxProps<Theme> = (theme) => ({
  position: 'absolute',
  top: theme.spacing(0.75),
  right: theme.spacing(0.75),
  display: 'flex',
});

export const codeFieldErrorIconSx: SxProps<Theme> = { display: 'flex', color: 'error.main' };

export const codeFieldCameraButtonSx: SxProps<Theme> = {
  width: 'auto',
  height: 'auto',
};
